// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import BundleTenantPhotoProduct from "./bundle-tenant-photo-product.js"
import Tenant from "./tenant.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": true
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": true
    },
    "name": {
      "column": null,
      "name": "name",
      "selected_by_default": null,
      "translated": true
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": true
    }
  },
  "className": "Bundle",
  "collectionKey": "bundles",
  "collectionName": "bundles",
  "i18nKey": "bundle",
  "camelizedLower": "bundle",
  "name": "Bundle",
  "nameDasherized": "bundle",
  "pluralName": "bundles",
  "ransackable_associations": [
    {
      "className": "BundleOrderLine",
      "collectionName": "bundle_order_lines",
      "foreignKey": "bundle_id",
      "name": "bundle_order_lines",
      "macro": "has_many",
      "resource_name": "BundleOrderLine",
      "through": null
    },
    {
      "className": "BundleTenantPhotoProduct",
      "collectionName": "bundle_tenant_photo_products",
      "foreignKey": "bundle_id",
      "name": "bundle_tenant_photo_products",
      "macro": "has_many",
      "resource_name": "BundleTenantPhotoProduct",
      "through": null
    },
    {
      "className": "Bundle::Translation",
      "collectionName": null,
      "foreignKey": "bundle_id",
      "name": "current_translation",
      "macro": "has_one",
      "resource_name": null,
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "PhotoProduct",
      "collectionName": "photo_products",
      "foreignKey": "resource_id",
      "name": "photo_products",
      "macro": "has_many",
      "resource_name": "PhotoProduct",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "Bundle::Translation",
      "collectionName": null,
      "foreignKey": "bundle_id",
      "name": "translations",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "BundleTenantPhotoProduct",
      "collectionName": "bundle_tenant_photo_products",
      "foreignKey": "bundle_id",
      "name": "bundle_tenant_photo_products",
      "macro": "has_many",
      "resource_name": "BundleTenantPhotoProduct",
      "through": null
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    }
  ],
  "paramKey": "bundle",
  "primaryKey": "id"
}

/** Frontend model for Bundle. */
export default class Bundle extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./bundle-tenant-photo-product.js").default>} */
  bundleTenantPhotoProducts() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["bundle_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "bundle_tenant_photo_products",
        model: this,
        modelClass: BundleTenantPhotoProduct
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./bundle-tenant-photo-product.js").default>>} */
  loadBundleTenantPhotoProducts() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["bundle_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "bundle_tenant_photo_products",
        model: this,
        modelClass: BundleTenantPhotoProduct
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./bundle-tenant-photo-product.js").default>>} */
  ensureBundleTenantPhotoProductsLoaded() {
    return this.ensureAssociationLoaded("bundle_tenant_photo_products")
  }

  /** @returns {import("./tenant.js").default | null} */
  tenant() {
    return this._readBelongsToReflection({modelClass: Tenant, reflectionName: "tenant"})
  }

  /** @returns {Promise<import("./tenant.js").default | null>} */
  loadTenant() {
    if (!("tenantId" in this)) throw new Error("Foreign key method wasn't defined: tenantId")

    const id = this.tenantId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "tenant",
        model: this,
        modelClass: Tenant
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./tenant.js").default | null>} */
  ensureTenantLoaded() {
    return this.ensureAssociationLoaded("tenant")
  }
}
